import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHasPermission } from "../../context/AuthContext";
import { fetchFlags, unflagCall } from "./supervision-api";
import { getManualFlags, removeManualFlag } from "./lib/flags-store";
import type { FlagsResponse, ReviewFlag, SupervisedCall } from "./types";
import { cn } from "../../lib/utils";
import { callColorRank } from "./lib/alerts";
import { useDemoMode } from "../../lib/demo-mode";
import { DEMO_SUPERVISED_CALLS } from "../../lib/demo-data";

export function FlaggedCalls({onSelect}: {onSelect: (call: SupervisedCall) => void}) {
  const isDemo = useDemoMode();
  const canTriage = useHasPermission("supervision.triage");
  const [cursor, setCursor] = useState<string | undefined>();
  const [page, setPage] = useState<FlagsResponse>({flags: [], next_cursor: null});
  const [manualFlags, setManualFlags] = useState<ReviewFlag[]>(getManualFlags);
  const [dismissedDemoFlagIds, setDismissedDemoFlagIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const invalidate = useCallback(() => { ++generation.current; }, []);
  const load = useCallback(async () => {
    const request = ++generation.current;
    try { const data = await fetchFlags(cursor); if (request === generation.current) {setPage(data); setError(null);} }
    catch (err) { if (request === generation.current) {
      setError((err as Error).message);
      if ([401, 403].includes((err as {status: number}).status)) {
        setPage({flags: [], next_cursor: null});
        if (!isDemo) window.dispatchEvent(new Event("supervision-access-denied"));
      }
    } }
  }, [cursor, isDemo]);
  useEffect(() => {
    void load(); const timer = setInterval(() => void load(), 20000);
    const refresh = () => {
      setManualFlags(getManualFlags());
      void load();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("supervision-flags-changed", refresh);
    return () => { invalidate(); clearInterval(timer); window.removeEventListener("focus", refresh);
      window.removeEventListener("supervision-flags-changed", refresh); };
  }, [load, invalidate]);

  const demoFlags = useMemo(() => {
    if (!isDemo) return [];
    return DEMO_SUPERVISED_CALLS
      .filter((c) => (c.severity === "critical" || c.severity === "warn") && !dismissedDemoFlagIds.includes(c.call_id))
      .map((c) => ({
        call_id: c.call_id,
        flagged_at: c.created_at,
        flagged_by: "supervisor_demo",
        flagged_by_name: "Floor Supervisor",
        note: c.alerts?.top?.detail || "Flagged for supervisor review and compliance check.",
        call: c as SupervisedCall,
      }));
  }, [isDemo, dismissedDemoFlagIds]);

  const remove = async (id: string) => {
    removeManualFlag(id);
    if (isDemo && !page.flags.some((f) => f.call_id === id)) {
      setDismissedDemoFlagIds((prev) => [...prev, id]);
      return;
    }
    setBusy(true);
    try { await unflagCall(id); await load(); window.dispatchEvent(new Event("supervision-flags-changed")); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  const activeFlags = useMemo(() => {
    const map = new Map<string, ReviewFlag>();
    // API flags first
    for (const f of page.flags) {
      if (!dismissedDemoFlagIds.includes(f.call_id)) map.set(f.call_id, f);
    }
    // Manual flags
    for (const f of manualFlags) {
      if (!dismissedDemoFlagIds.includes(f.call_id) && !map.has(f.call_id)) map.set(f.call_id, f);
    }
    // Demo flags
    if (isDemo) {
      for (const f of demoFlags) {
        if (!dismissedDemoFlagIds.includes(f.call_id) && !map.has(f.call_id)) map.set(f.call_id, f);
      }
    }
    return Array.from(map.values());
  }, [page.flags, manualFlags, demoFlags, isDemo, dismissedDemoFlagIds]);
  const sortedFlags = [...activeFlags].sort((a, b) => (
    (a.call ? callColorRank(a.call) : 3) - (b.call ? callColorRank(b.call) : 3)
  ));
  const displayError = isDemo && demoFlags.length > 0 ? null : error;

  return <section className="min-w-0 flex-1 overflow-auto p-4">
    <h2 className="text-lg font-semibold mb-3">Flagged calls</h2>
    {displayError && <p role="alert" className="text-danger">{displayError}</p>}
    {!activeFlags.length && <p className="text-sm text-ink-3">No flagged calls on this page.</p>}
    <div className="space-y-3">{sortedFlags.map(flag => {
      const tone = flag.call ? callColorRank(flag.call) : 3;
      return <article key={flag.call_id} className={cn(
        "rounded-xl border border-l-2 p-4",
        tone === 0
          ? "border-2 border-danger bg-elevated"
          : tone === 1
            ? "border-2 border-warning bg-elevated"
            : tone === 2
              ? "border-line border-l-ink-3/40 bg-soft/65"
              : "border-line border-l-line bg-elevated",
      )}>
      <button className="font-medium text-brand" onClick={() => onSelect(flag.call ?? {call_id: flag.call_id})}>
        {flag.call?.contact_name || flag.call_id}
      </button>
      <p className="text-xs text-ink-3">{flag.call?.agent_name} {flag.call?.campaign} · {flag.call?.lifecycle === "live" ? "Live now" : flag.call?.lifecycle === "ended_complete" ? "Call ended" : "Call detail pending or unavailable"}</p>
      {flag.call?.created_at && <p className="text-xs">Call: {new Date(flag.call.created_at).toLocaleString()}</p>}
      <p className="text-xs text-ink-3">Call {flag.call?.call_id || flag.call_id}{flag.call?.schedule_id ? ` · Schedule ${flag.call.schedule_id}` : ""}{flag.call?.contact_id ? ` · Contact ${flag.call.contact_id}` : ""}</p>
      <p className="text-xs text-ink-3">Flagged by {flag.flagged_by_name || flag.flagged_by} · {new Date(flag.flagged_at).toLocaleString()}</p>
      {flag.note && <p className="whitespace-pre-wrap text-sm mt-2">{flag.note}</p>}
      {canTriage && <button disabled={busy} className="text-xs text-brand mt-2" onClick={() => void remove(flag.call_id)}>Unflag</button>}
    </article>})}</div>
    <div className="flex gap-4 mt-4">
      {cursor && <button onClick={() => setCursor(undefined)}>Newest</button>}
      {page.next_cursor && <button onClick={() => setCursor(page.next_cursor!)}>Next page</button>}
    </div>
  </section>;
}
